'use strict';
/* ==========================================================================
   levels/pen.js — step "pen" (orientation any, notebook style): RED PEN (DESIGN.md §5.2).

   Six of Jay's homework cards per attempt from the pool `pen` (content/bank.pen.json through Scale.pool → core/bank.js:
   draw.n = 6, draw.unique = "cheat" so no cheat twice, draw.require = ["legit"] so exactly one honest card), served
   easiest first (Scale.byLevel). Every card is built by the model: Scale.card(item, params) → the equation, Jay's
   numbered lines (the last a check line), each line's property and move, the first wrong line (bad; −1 = all correct),
   the honest lines, and the fix (honest / wrong / other line, property, act, whyWrong). Every number and equation on
   screen comes from it; the follow-up options are the bank instance's (shuffled; for a cheat card: the honest line,
   Jay's line, another misconception's line, all generated by the model through Scale.pool).

   A card drops onto the page: an index card, "Jay's homework" + the equation on the red line, the numbered lines on
   the ruled lines.
   Per card:  drop → spot: tap the FIRST wrong line, or "All correct". A tap on an honest line is a miss ("Line 1 is
                    fine: subtract 4 from both sides (subtraction property of equality)."); a line after the wrong one
                    or the failing check is a miss too ("the mistake is earlier"); after 2 misses Ms. Rivera circles
                    it herself. "?" beside a line shows its property and move for free (never costs a star).
            → value: "What should that line say?" (legit: the card's own follow-up); a wrong pick is crossed out
                    with its `why`, pick again
            → fix: Rivera's red pen strikes the wrong line and writes the honest one above it with its property;
                   every later line gets its honest version; the check line is rewritten and balances (a red tick).
                   Her card: the wrong line, the model's why, the move and property, the honest check → next card.
   Stars: right line first try = 1 + right pick first try = 1 (legit: "All correct" first try = 1 + follow-up = 1).
   of = 12, pass = pool.passStars (8). Game.progress.scores.pen = {best, last, of}; first answers under
   Game.progress.answers['pen/<key>/line'] and ['pen/<key>']; Bank.finish at the end; the stamp (Game.complete)
   through "Collect your stamp" on a pass (a pass that leaves without collecting keeps it). Resume:
   Game.progress.bank.pen.run = {i, ev, res} (the current card's taps / checks / picks are replayed after a reload).

   Portrait phone (Dock.active): the stage shows the card large (a picture); the lines are Dock.choices rows,
   "All correct" a dock button, the "?" property checks dock buttons, the follow-up Dock.choices with the why as
   notes, the fix a list. Landscape phone: the lines / "?" are canvas tap targets ≥ 44 CSS px (UI.tap).
   Keys: 1–4 = line n, A = All correct, 1–3 = option, Enter = next.

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'value' | 'fix' | 'end'), sc.card (the Scale.card result),
   sc.answer() (the bad line index or 'legit'), sc.tapLine(i), sc.legit(), sc.choose(i), sc.check(i), sc.next(),
   sc.begin(fresh), sc.collect(), sc.back(), sc.finish() (tests: end now + stamp); sc.inst, sc.instances, sc.bi,
   sc.results, sc.stars, sc.seed, sc.attempt, sc.resumed, sc.misses, sc.picks, sc.marks, sc.checked;
   Levels.pen.solution ({legit, line, answer, value, valueText}), Levels.pen.lines() (the tap rects of the live card's
   lines on the canvas), Levels.pen.pool() (the bank copy passed to Bank.attempt), Levels.pen.round(inst).
   ========================================================================== */
(function () {
  const STEP = 'pen';
  const MAX_MISSES = 2, DROP = 0.7, NEXT_GUARD = 0.3;
  const pool = () => {
    const p = Bank.pool(STEP);
    return p && p.items.length ? p : Bank.load(Scale.pool(Game.content.bank_pen || { pool: STEP, draw: { n: 0 }, items: [] }));
  };
  const SHORT = { legit: 'all correct', oneSide: 'done to one side only', partDivide: 'divided only part of a side',
    partDistribute: 'distributed to one term only', signSlip: 'lost a negative sign', wrongInverse: 'subtracted instead of dividing',
    moveNoSign: 'moved a number, kept its sign' };
  const INK = () => NB.PAL.ink, RED = () => NB.PAL.redpen, PENCIL = () => NB.PAL.pencil;
  const uiF = (s, w = 400) => `${w} ${s}px ${NB.FONT.ui}`, handF = (s, w = 400) => `${w} ${s}px ${NB.FONT.hand}`;
  const tw = (s, f) => { g.save(); g.font = f; const w = g.measureText(s).width; g.restore(); return w; };
  const last = a => a[a.length - 1];
  const lower1 = s => (s ? s[0].toLowerCase() + s.slice(1) : s);
  // UI._wrap, but never breaks next to an operator: an equation ("2 × 4 + 6 = 14") stays on one line
  const OP = /^[×+−=÷/-]$/;
  const wrapM = (text, maxW, font) => {
    const words = [];
    for (const t of String(text).split(/\s+/)) { const p = words.length ? words[words.length - 1] : null; if (p != null && (OP.test(t) || OP.test(p.slice(p.lastIndexOf(' ') + 1)))) words[words.length - 1] = p + ' ' + t; else words.push(t); }
    g.save(); g.font = font;
    const out = []; let line = '';
    for (const w of words) { const test = line ? line + ' ' + w : w; if (line && g.measureText(test).width > maxW) { out.push(line); line = w; } else line = test; }
    if (line) out.push(line);
    g.restore(); return out;
  };
  const unCheck = s => String(s).replace(/^Check:\s*/, '');

  /* one card: the model's card + the bank instance */
  function srcOf(inst) {
    const J = Game.content.bank_pen || { items: [] };
    return (J.items || []).find(x => x.id === inst.id) || inst.item;
  }
  function round(inst) {
    const C = Scale.card(srcOf(inst), inst.params);
    return { inst, C, legit: C.bad < 0, bad: C.bad, n: C.lines.length, title: C.title };
  }
  /* a line's property (free): the move + the property; the check line: substitution */
  function propOf(C, i) {
    const l = C.lines[i];
    if (l.op === 'check') return { tag: l.property || 'substitution', text: `The check: put ${C.v} = ${Scale.num(C.answer, C.eq.style)} back into ${C.text} (${l.property || 'substitution'}).` };
    return { tag: l.property, text: `Line ${i + 1}: ${l.act} (${l.property}).` };
  }
  /* why a tapped line is not the answer */
  function missText(R, i) {
    const C = R.C, l = C.lines[i];
    if (R.legit || i < R.bad) {
      if (l.op === 'check') return `The check is fine: ${unCheck(l.text)}.`;
      return `Line ${i + 1} is fine: ${lower1(l.act)} (${l.property}).`;
    }
    if (l.op === 'check') return 'The check fails, so a line above it is wrong. Find the first one.';
    return `Line ${i + 1} follows from the line before it. The mistake is earlier: find the FIRST wrong line.`;
  }
  const optFont = (R, s) => (R.legit ? uiF(s, 700) : handF(s, 700));

  /* ---------------- layouts (mode: 'm' mouse / desktop, 't' touch landscape, 'd' the portrait stage picture) ---------------- */
  function cardLay(R, mode) {
    const n = R.n, docked = mode === 'd', C = R.C;
    let x, y, w, head, rowH;
    if (docked) { x = 40; y = 80; w = 1840; head = 190; rowH = Math.min(230, Math.floor((1064 - y - head - 16) / n)); }
    else { x = mode === 't' ? 150 : 180; y = 200; w = mode === 't' ? 940 : 900; head = 150; rowH = mode === 't' ? Math.max(130, Math.ceil(UI.tap(64)) + 10) : 130; }
    const h = head + n * rowH + 16;
    if (!docked && y + h > 1068) y = Math.max(92, 1068 - h);
    const r = docked ? 0 : Math.round(UI.tap(52) / 2);
    const numX = x + (docked ? 60 : 46), tx = x + (docked ? 130 : 90);
    const base = docked ? 84 : Math.round(UI.text(48));
    const room = x + w - 30 - tx;
    const widest = Math.max(...C.lines.map(l => tw(l.text, handF(base, 700))));
    const size = Math.max(docked ? 40 : 24, Math.floor(Math.min(base, base * room / Math.max(1, widest))));
    // the red corrections (written above the struck lines): the honest lines, the bad one with its property
    const rbase = Math.round(size * 0.78);
    const reds = C.honest.map((l, k) => l.text + (k === C.bad ? `  (${l.property})` : ''));
    const rwide = Math.max(...reds.map(t => tw(t, handF(rbase, 700))), 1);
    const rsize = Math.max(docked ? 34 : 20, Math.floor(Math.min(rbase, rbase * room / rwide)));
    const psize = docked ? 34 : Math.round(Math.min(28, UI.text(22)));
    const rows = C.lines.map((l, i) => { const top = y + head + i * rowH; return { i, top, h: rowH, cy: Math.round(top + rowH * 0.62), ry: Math.round(top + rowH * 0.22), cx: x + w / 2, w }; });
    const eqSize = docked ? 96 : Math.round(Math.min(72, UI.text(60)));
    const chkX = x + w + 16 + r;
    return { mode, x, y, w, h, head, rowH, rows, r, numX, tx, size, rsize, psize, eqSize, chkX, panelX: docked ? 1900 : Math.max(1170, chkX + r + 34) };
  }

  class Pen extends Scene {
    get pool() { return pool(); }
    get n() { return this.instances ? this.instances.length : this.pool.draw.n; }
    get of() { return this.n * 2; }
    get passStars() { const j = this.pool.json || Game.content.bank_pen || {}; return Math.min(this.of, j.passStars || Math.ceil(this.of * 2 / 3)); }
    get inst() { return this.R ? this.R.inst : null; }
    get card() { return this.R ? this.R.C : null; }
    get state() { return this.phase; }
    get solution() { const R = this.R; return R ? { legit: R.legit, line: R.bad, answer: R.legit ? 'legit' : R.bad, value: R.inst.answerIndex, valueText: R.inst.options[R.inst.answerIndex] } : null; }
    answer() { return this.R ? (this.R.legit ? 'legit' : this.R.bad) : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }

    enter() {
      this.phase = 'intro'; this.instances = null; this.R = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Bank.attempt(STEP, pool(), { fresh });
      this.instances = Scale.byLevel(a.instances);                                   // easiest first (stable)
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startCard(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.instances.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.boardAt = Game.time - DROP - 1; this.phase = 'spot'; this.landed = true;
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 't') this.tapLine(v); else if (k === 'l') this.legit(); else if (k === 'c') this.choose(v); else if (k === 'k') this.check(v); }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[pen] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.R = round(this.instances[i]); this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = {}; this.checked = {}; this.lastCheck = -1; this.found = -1; this.revealed = false;
      this.picks = []; this.say = null; this.lineStar = 0; this.landed = false; this._fc = null;
      this.boardAt = Game.time; this.foundAt = this.pAt = this.fixAt = this.pickedAt = null; this.lastCheckAt = -1e9;
    }
    startCard(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx('whoosh', { gain: 0.35 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && (!Audio.has || Audio.has(name))) Audio.play(name, o); }
    pen() { this.sfx(Audio.has && Audio.has('pen') ? 'pen' : 'scribble', { gain: 0.45 }); }

    /* ---- ?: a line's property and move, free (never an answer, never costs a star) ---- */
    check(i) {
      const R = this.R;
      if (!R || !(i >= 0 && i < R.n) || this.phase === 'intro' || this.phase === 'end' || this.phase === 'fix') return false;
      if (this.phase === 'drop') this.phase = 'spot';
      this.ev.push(['k', i]);
      this.checked[i] = this.checked[i] || { at: Game.time };
      this.lastCheck = i; this.lastCheckAt = Game.time;
      this.sfx(Audio.has && Audio.has('beep') ? 'beep' : 'pop', { gain: 0.3 });
      this.save();
      return true;
    }
    property(i) { return this.R ? propOf(this.R.C, i) : null; }

    /* ---- spot the first wrong line ---- */
    tapLine(i) {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R || !(i >= 0 && i < R.n) || this.marks[i]) return false;
      this.ev.push(['t', i]);
      Game.progress.answer(STEP + '/' + R.inst.key + '/line', `${i + 1}. ${R.C.lines[i].text}`);   // first try only
      if (!R.legit && i === R.bad) { this.lineStar = this.misses === 0 ? 1 : 0; this.foundLine(i, false); this.save(); return true; }
      this.marks[i] = { at: Game.time, fine: R.legit || i < R.bad };
      this.miss(missText(R, i));
      this.save();
      return true;
    }
    legit() {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R) return false;
      this.ev.push(['l']);
      Game.progress.answer(STEP + '/' + R.inst.key + '/line', 'All correct');       // first try only
      if (R.legit) { this.lineStar = this.misses === 0 ? 1 : 0; this.toValue(); this.sfx('ding', { gain: 0.6 }); this.save(); return true; }
      this.miss(`Not all correct: the check gives ${unCheck(R.C.check.text)}. A line is wrong.`);
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time };
      this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        const R = this.R;
        this.lineStar = 0;
        if (R.legit) { this.say = { text: 'Every line checks out: this card is all correct.', at: Game.time }; this.revealed = true; this.toValue(); }
        else { this.say = { text: `Ms. Rivera circles line ${R.bad + 1}: the first wrong line.`, at: Game.time }; this.foundLine(R.bad, true); }
      }
    }
    foundLine(i, revealed) {
      this.found = i; this.revealed = revealed; this.foundAt = Game.time;
      if (!revealed) this.say = null;
      this.pen();
      this.toValue(0.6);
    }
    toValue(delay = 0.4) { this.phase = 'value'; this.pAt = Game.time + delay; }

    /* ---- what should that line say? (legit: the card's follow-up) ---- */
    choose(i) {
      const q = this.inst;
      if (this.phase !== 'value' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + q.key, q.options[i]);   // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      if (Bank.check(q, i)) {
        const fStar = this.picks.length === 1 ? 1 : 0;
        this.sfx('ding', { gain: 0.7 });
        this.toFix(this.lineStar, fStar);
      } else this.sfx('thunk', { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(lineStar, fixStar) {
      const R = this.R;
      this.phase = 'fix'; this.fixAt = Game.time; this._fc = null;
      this.results[this.bi] = { id: R.inst.id, key: R.inst.key, cheat: R.C.cheat, legit: R.legit, title: R.C.title, misses: this.misses,
        revealed: this.revealed, wrongPicks: this.picks.length - 1, lineStar, fixStar, stars: lineStar + fixStar };
      this.pen();
    }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      if (this.bi < this.instances.length - 1) this.startCard(this.bi + 1);
      else this.end();
      return true;
    }
    end() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Bank.finish(STEP);                                                             // the next attempt draws new cards
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step (tools/integration.js §5) */
    finish() {
      if (this.collected) return;
      if (!this.instances) this.begin();
      if (this.phase !== 'end') this.end();
      this.collected = true; Game.complete(STEP);
    }
    done() { this.finish(); }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new cards)', go: () => this.begin() }]
        : [{ label: 'Try again (new cards)', go: () => this.begin() }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.boardAt > DROP + 0.1) this.phase = 'spot';
      if (this.R && !this.landed && Game.time - this.boardAt > DROP * 0.7) { this.landed = true; this.sfx('paper', { gain: 0.45, rate: 1.1 }); }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') { if (n >= 1 && n <= 9) this.tapLine(n - 1); else if (k === 'a' || k === 'A') this.legit(); }
      else if (this.phase === 'value') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm', key = mode + '|' + Math.round(UI.tap(64)) + '|' + Math.round(UI.text(48)) + '|' + this.bi;
      let L = this.lays.get(key);
      if (!L) { L = cardLay(this.R, mode); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.instances || this.phase === 'intro' || this.phase === 'end') return '';
      return `card ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      NB.ground();
      UI.hud('Red Pen', this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked), R = this.R;
      const s = E.outBack(Math.max(0, Math.min(1, (T - this.boardAt) / DROP)), 1.1);
      g.save(); g.translate(0, (1 - s) * -1000); g.rotate((1 - s) * -0.05);
      this.drawCard(R, L, this.cardState(docked));
      g.restore();
      if (docked) return this.drawDock(L);
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot(L);
      else if (this.phase === 'value') this.drawValue(L);
      else this.drawFix(L);
    }
    cardState(docked) {
      const spot = this.phase === 'spot' || this.phase === 'drop', fix = this.phase === 'fix';
      return { docked, spot, live: this.phase === 'spot' && !docked, marks: this.marks, checked: this.checked, found: this.found, foundAt: this.foundAt,
        fix, fixAt: this.fixAt, bi: this.bi, show: this.lastCheck, chips: !docked && (spot || this.phase === 'value') };
    }

    /* ---- Jay's card: "Jay's homework", the equation, the numbered lines; the red pen in the fix ---- */
    drawCard(R, L, st) {
      const C = R.C, { x, y, w, h, head, rowH, size, rsize, psize, tx, numX } = L, docked = L.mode === 'd';
      NB.indexCard(x, y, w, h, { lines: false });
      g.save();
      g.fillStyle = RED(); g.fillRect(x, y + head - 1, w, 2.5);
      g.fillStyle = NB.PAL.cardLine;
      for (let i = 1; i <= R.n; i++) g.fillRect(x, y + head + i * rowH - 1, w, 1.5);
      g.restore();
      const hs = docked ? 38 : Math.round(Math.min(28, UI.text(24)));
      NB.write(`Jay's homework: ${C.title}`, x + 30, y + head * 0.22, { font: 'ui', weight: 700, size: hs, color: PENCIL() });
      NB.write(`Solve ${C.text}`, x + 30, y + head * 0.64, { size: L.eqSize, weight: 700 });
      for (const row of L.rows) {
        const l = C.lines[row.i];
        if (st.live) {
          const hit = UI._hit('pn-row' + st.bi + '_' + row.i, row.cx, row.top + row.h / 2, w, row.h);
          if (hit.hover && !st.marks[row.i]) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = NB.PAL.hi; g.fillRect(x + 6, row.top + 4, w - 12, row.h - 8); g.restore(); }
          if (hit.clicked) this.tapLine(row.i);
        }
        const mark = st.marks[row.i];
        NB.write(String(row.i + 1), numX, row.cy, { font: 'ui', weight: 700, size: Math.round(size * 0.66), color: PENCIL(), align: 'center' });
        const cw = NB.write(l.text, tx, row.cy, { size, weight: 700, color: mark ? PENCIL() : INK() });
        if (mark && !st.fix) {                           // a miss: a pencil verdict at the right, above the line
          const label = mark.fine ? 'fine' : 'not the first wrong line', lw = tw(label, uiF(psize)), lx = x + w - 30 - lw;
          NB.write(label, lx, row.ry, { font: 'ui', size: psize, color: PENCIL(), t0: mark.at, dur: 0.3 });
          if (mark.fine) NB.tick(lx - psize * 1.1, row.ry, { s: docked ? 1.0 : 0.6, color: PENCIL(), t0: mark.at });
        }
        // the property (free), in pencil above the line
        if (st.checked[row.i] && !st.fix) NB.write(propOf(C, row.i).tag, tx, row.ry, { font: 'ui', size: psize, color: PENCIL(), t0: st.checked[row.i].at, dur: 0.3 });
        if (st.chips && UI.iconButton('pn-chk' + st.bi + '_' + row.i, '?', L.chkX, row.cy, { r: L.r, on: st.show === row.i })) this.check(row.i);
      }
      if (st.found >= 0) {
        const row = L.rows[st.found], cw = tw(C.lines[st.found].text, handF(size, 700));
        NB.circle(tx + cw / 2, row.cy + rowH * 0.02, cw / 2 + 26, rowH * 0.25, { t0: st.foundAt, dur: 0.4 });
      }
      if (!st.fix) return;
      const t0 = st.fixAt + 0.35;
      if (R.legit) { L.rows.forEach((row, k) => NB.tick(x + w - (docked ? 70 : 44), row.cy, { s: docked ? 1.5 : 1, t0: t0 + k * 0.15 })); return; }
      // Rivera's red pen: strike every line from the wrong one on; write the honest line above it
      let tt = t0;
      for (let k = R.bad; k < R.n; k++) {
        const row = L.rows[k], jw = tw(C.lines[k].text, handF(size, 700)), bad = k === R.bad, chk = k === R.n - 1;
        NB.strike(tx - 6, row.cy + size * 0.12, tx + jw + 8, row.cy - size * 0.12, { t0: tt, w: bad ? (docked ? 8 : 6) : (docked ? 5 : 4), color: bad ? RED() : PENCIL() });
        const hl = C.honest[k];
        const hwid = NB.write(hl.text, tx + 6, row.ry, { size: rsize, weight: 700, color: RED(), t0: tt + 0.25, dur: 0.45 });
        if (bad) NB.write(`(${hl.property})`, tx + 6 + hwid + rsize * 0.6, row.ry, { size: Math.round(rsize * 0.8), color: RED(), t0: tt + 0.7, dur: 0.4 });
        if (chk) NB.tick(Math.min(tx + 6 + hwid + 44, x + w - 30), row.ry, { s: docked ? 1.3 : 0.85, t0: tt + 0.75 });
        tt += bad ? 1.0 : 0.6;
      }
    }

    /* ---------------- the right-hand panel (desktop / landscape) ---------------- */
    drawSpot(L) {
      const X = L.panelX, W = 1880 - X, hs0 = UI.hudSlot(0), top = Math.max(200, hs0.y + hs0.r + 30), bh = UI.tap(92);
      // the latest: a miss (red note) or a property (pencil)
      const sayNew = this.say && (this.lastCheck < 0 || this.say.at >= this.lastCheckAt) && T - this.say.at < 8;
      const info = sayNew ? this.say.text : this.lastCheck >= 0 ? propOf(this.R.C, this.lastCheck).text : '';
      const all = ['Is every line right?', 'Each line should do the same move to both sides, to every part.', 'Tap the FIRST wrong line, or All correct.', 'Tap ? beside a line to see its property (free).'];
      let P;
      for (const [k, nb] of [[1, 4], [1, 3], [0.85, 3], [0.75, 2], [0.65, 2]]) {       // shrink until the panel fits the page
        const fs = UI.text(30) * k, hs = UI.text(44) * k, ns = UI.text(30) * k, body = all.slice(0, nb);
        const wrapped = body.flatMap((l, i) => UI._wrap(l, W - 70, uiF(fs, i ? 400 : 700)).map(t => [t, i]));
        const head = hs * 1.8, h = head + wrapped.length * fs * 1.35 + 24;
        const iL = info ? wrapM(info, W - 40, sayNew ? handF(ns) : uiF(ns * 0.93)) : [];
        const total = top + h + 26 + bh + 34 + iL.length * ns * 1.28;
        P = { fs, hs, ns, wrapped, head, h, iL };
        if (total <= 1070) break;
      }
      const { fs, hs, ns, wrapped, head, h, iL } = P;
      NB.indexCard(X, top, W, Math.round(h), { header: Math.round(head), pitch: Math.round(fs * 1.35) });
      NB.write(`Card ${this.bi + 1} of ${this.n}`, X + 34, top + head * 0.5, { size: Math.round(hs), weight: 700, t0: this.boardAt });
      wrapped.forEach(([t, i], k) => NB.write(t, X + 34, top + head + (k + 0.5) * fs * 1.35 + 6, { font: 'ui', weight: i ? 400 : 700, size: Math.round(fs), color: i === 3 ? PENCIL() : INK(), t0: this.boardAt + 0.2 + k * 0.08, dur: 0.25 }));
      const by = top + h + 26 + bh / 2;
      if (Game.time - this.boardAt > 0.5 && UI.button('pn-legit', UI.hint('A:  ', '') + 'All correct', X + W / 2, by, { w: Math.min(W - 40, 520), h: 92, secondary: true })) this.legit();
      const y = by + bh / 2 + 34;
      if (sayNew) iL.forEach((t, k) => NB.note(t, X + 20, y + k * ns * 1.28, -0.01, { t0: this.say.at + k * 0.3, size: Math.round(ns), dur: 0.35 }));
      else iL.forEach((t, k) => NB.write(t, X + 20, y + k * ns * 1.28, { font: 'ui', size: Math.round(ns * 0.93), color: PENCIL(), t0: this.lastCheckAt + k * 0.1, dur: 0.3 }));
    }
    valueCard(L) {
      const X = L.panelX, W = 1880 - X, q = this.inst, R = this.R, qs = UI.text(36), os0 = UI.text(42);
      const intro = this.revealed ? (R.legit ? 'It was all correct.' : `Ms. Rivera circled line ${R.bad + 1}.`) : R.legit ? 'Right: all correct!' : `Got it: line ${R.bad + 1} is the first wrong line.`;
      const hs0 = UI.hudSlot(0), top0 = hs0.y + hs0.r + 16;
      const lp = last(this.picks), why = lp != null && lp !== q.answerIndex ? q.why[lp] : '';
      const hint = R.legit ? '' : `Line ${R.bad + 1} should ${lower1(R.C.lines[R.bad].act)}. Do it to the line above.`;
      const optW = W - 130;
      let C;
      for (const [withHint, k] of [[true, 1], [false, 1], [false, 0.85], [false, 0.72]]) {
        const os = Math.floor(Math.min(os0, ...q.options.map(o => os0 * optW / Math.max(1, tw(o, optFont(R, os0))))) * Math.max(k, 0.85));
        const rows = q.options.map(() => Math.max(UI.tap(92) + 4, os * 1.9));
        const is = UI.text(30) * k, iL = UI._wrap(intro, W - 80, handF(is, 700)), qL = UI._wrap(q.text, W - 80, uiF(qs * k, 700));
        const head = 40 + iL.length * is * 1.3 + qL.length * qs * k * 1.25;
        const ws = UI.text(30) * k, whyL = why ? wrapM(why, W - 80, handF(ws)) : [];
        const hs = UI.text(26) * k, hintL = withHint && hint ? UI._wrap(hint, W - 80, uiF(hs)) : [];
        const h = head + rows.reduce((a, b) => a + b, 0) + 20 + hintL.length * hs * 1.3 + 10 + whyL.length * ws * 1.3 + (whyL.length ? 16 : 0);
        C = { X, W, q, qs: qs * k, os, qL, head, rows, whyL, ws, hintL, hs, h, top: Math.max(top0, Math.min(200, 1070 - h)), iL, is };
        if (top0 + h <= 1070) break;
      }
      return C;
    }
    drawValue(L) {
      const C = this.valueCard(L), { X, W, q, qs, os, qL, head, rows, whyL, ws, hintL, hs, top, iL, is, h } = C, t0 = this.pAt, R = this.R;
      if (T < t0) return;
      NB.indexCard(X, Math.round(top), W, Math.round(h), { header: Math.round(head), pitch: 1e4 });
      iL.forEach((l, i) => NB.note(l, X + 34, top + 20 + (i + 0.5) * is * 1.3, 0, { t0, size: is, weight: 700, dur: 0.3 }));
      const qy = top + 20 + iL.length * is * 1.3;
      qL.forEach((l, i) => NB.write(l, X + 34, qy + (i + 0.5) * qs * 1.25, { font: 'ui', weight: 700, size: qs, t0: t0 + 0.2 + i * 0.15, dur: 0.3 }));
      let y = top + head;
      const lp = last(this.picks);
      q.options.forEach((o, i) => {
        const rh = rows[i], cy = y + rh / 2, kx = X + 60, crossed = this.picks.includes(i);
        g.save(); g.fillStyle = NB.PAL.cardLine; g.fillRect(X, y + rh - 1, W, 1.5); g.restore();
        const hit = !crossed ? UI._hit('pn-opt' + this.bi + '_' + i, X + W / 2, cy, W - 20, rh - 2) : { hover: false, clicked: false };
        if (hit.hover) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = NB.PAL.hi; g.fillRect(X + 12, y + 6, W - 24, rh - 12); g.restore(); }
        g.save(); g.lineWidth = 3; g.strokeStyle = INK(); g.strokeRect(kx - 20, cy - 20, 40, 40);
        g.font = uiF(24, 700); g.fillStyle = INK(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), kx, cy + 1); g.restore();
        const mw = NB.write(o, kx + 50, cy, { size: os, weight: 700, font: R.legit ? 'ui' : 'hand', color: crossed ? PENCIL() : INK(), t0: t0 + 0.2 + i * 0.1, dur: 0.25 });
        if (crossed) NB.strike(kx + 40, cy - 16, kx + 60 + mw, cy + 16, { t0: i === lp ? this.pickedAt : -1e9, w: 5 });
        if (hit.clicked) this.choose(i);
        y += rh;
      });
      hintL.forEach((l, k) => NB.write(l, X + 34, y + 20 + (k + 0.5) * hs * 1.3, { font: 'ui', size: hs, color: PENCIL(), t0: t0 + 0.5 + k * 0.2, dur: 0.3 }));
      y += 20 + hintL.length * hs * 1.3 + 10;
      whyL.forEach((l, k) => NB.note(l, X + 34, y + (k + 0.5) * ws * 1.3, 0, { t0: this.pickedAt + 0.3 + k * 0.3, size: ws, dur: 0.35 }));
    }
    /* Rivera's card: the wrong line, the model's why, the move + property, the honest check, this card's stars */
    fixLines() {
      const R = this.R, C = R.C, q = this.inst, res = this.results[this.bi] || { stars: 0, lineStar: 0, fixStar: 0 }, out = [];
      if (R.legit) {
        out.push(['head', res.lineStar ? 'All correct!' : 'It was all correct.']);
        out.push(['work', `Answer: ${q.options[q.answerIndex]}.`]);
        out.push(['rule', q.explain || '']);
      } else {
        const f = C.fix, k = R.bad + 1;
        out.push(['head', "Ms. Rivera's red pen"]);
        out.push(['bad', `Line ${k}: Jay wrote ${f.wrong}`]);
        out.push(['rule', f.whyWrong]);
        out.push(['work', `Line ${k}: ${f.act} (${f.property}).`]);
        out.push(['work', `${last(C.honest).text}. Balanced.`]);
      }
      out.push(['stars', `+${res.stars} star${res.stars === 1 ? '' : 's'} (${R.legit ? 'all correct' : 'line'} ${res.lineStar}, ${R.legit ? 'question' : 'fix'} ${res.fixStar})`]);
      return out;
    }
    fixCard(L) {
      const key = this.bi + '|' + Math.round(UI.text(28)) + '|' + L.panelX;
      if (this._fc && this._fc.key === key) return this._fc;
      const F = this.fixLines(), bh = UI.tap(92), hs0 = UI.hudSlot(0), top0 = hs0.y + hs0.r + 24, X = L.panelX, W = 1880 - X;
      let C;
      for (const k of [1, 0.9, 0.8, 0.72, 0.64]) {
        const S = { head: [UI.text(44) * k, handF(UI.text(44) * k, 700)], bad: [UI.text(32) * k, handF(UI.text(32) * k, 700)], work: [UI.text(28) * k, uiF(UI.text(28) * k)],
          rule: [UI.text(32) * k, handF(UI.text(32) * k)], stars: [UI.text(34) * k, handF(UI.text(34) * k, 700)] };
        const lh = kk => S[kk][0] * 1.32;
        const rows = F.flatMap(([kk, t]) => wrapM(t, W - 70, S[kk][1]).map((l, j) => [kk, l, j > 0]));
        const h = Math.round(rows.reduce((a, [kk]) => a + lh(kk), 0) + 36);
        C = { key, X, W, rows, h, S, lh, bh, top: Math.round(Math.max(96, Math.min(top0, 1080 - 14 - bh - 22 - h))) };
        if (top0 + h + 22 + bh + 14 <= 1080) break;
      }
      return (this._fc = C);
    }
    drawFix(L) {
      const C = this.fixCard(L), { X, W, rows, h, S, lh, bh, top } = C, t0 = this.fixAt + 0.2;
      NB.indexCard(X, top, W, h, { header: Math.round(rows.filter(([k]) => k === 'head').length * lh('head') + 20), pitch: 1e4 });
      let y = top + 18, tt = t0, wasHead = true;
      rows.forEach(([k, l]) => {
        if (wasHead && k !== 'head') { y += 6; wasHead = false; }
        const s = S[k][0], cy = y + lh(k) / 2 + (k === 'head' ? 2 : 8);
        const o = { size: s, t0: tt, dur: Math.max(0.2, Math.min(0.5, l.length * 0.02)) };
        if (k === 'head') NB.write(l, X + 34, cy, { ...o, weight: 700, color: RED() });
        else if (k === 'rule') NB.note(l, X + 34, cy, 0, o);
        else if (k === 'bad') NB.write(l, X + 34, cy, { ...o, weight: 700, color: PENCIL() });
        else if (k === 'work') NB.write(l, X + 34, cy, { ...o, font: 'ui', color: INK() });
        else NB.write(l, X + 34, cy, { ...o, weight: 700, color: RED() });
        tt += o.dur * 0.6; y += lh(k);
      });
      const label = this.bi < this.n - 1 ? 'Next card' : 'See my stars';
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('pn-next', label, X + W / 2, top + h + 22 + bh / 2, { w: Math.min(W - 40, 440), h: 92, primary: true })) this.next();
    }

    /* ---------------- portrait: the card is the picture; everything else in the dock ---------------- */
    drawDock(L) {
      const R = this.R, q = this.inst, id = this.attempt + '_' + this.bi, C = R.C;
      Dock.head(`Card ${this.bi + 1} of ${this.n}`, `${this.stars} of ${this.of} stars`);
      const opts = C.lines.map(l => l.text);
      const notes = C.lines.map((l, i) => (this.marks[i] ? (this.marks[i].fine ? 'fine' : 'not the first wrong line') : this.checked[i] ? propOf(C, i).tag : ''));
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('pn-how', ['Each line should do the same move to both sides, to every part. Tap the FIRST wrong line, or All correct.'], { cls: 'note' });
        if (this.phase === 'spot') {
          Dock.choices('pn-lines' + id, { prompt: `Jay's homework: solve ${C.text.replace(/ /g, ' ')}`, options: opts, state: C.lines.map((l, i) => (this.marks[i] ? 'faded' : '')), notes, onChoose: i => this.tapLine(i) });
          Dock.buttons('pn-legit' + id, [{ label: 'All correct', wide: true, onClick: () => this.legit() }]);
          if (this.say) Dock.text('pn-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
          this.dockChecks(id, R.n);
        }
        return;
      }
      if (this.phase === 'value') {
        const found = this.found >= 0 ? (this.revealed ? 'Ms. Rivera circled line ' : 'You circled line ') + (this.found + 1) + ': ' + opts[this.found] : (this.revealed ? 'It was all correct.' : 'Right: all correct!');
        Dock.text('pn-found' + id, [found], { cls: this.revealed ? 'note' : 'right' });
        const st = q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        Dock.choices('pn-val' + id, { prompt: q.text, options: q.options.slice(), state: st, notes: q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : '')), onChoose: i => this.choose(i) });
        if (!R.legit) this.dockChecks(id, R.n);
        return;
      }
      // fix
      const F = this.fixLines();
      Dock.choices('pn-val' + id, { prompt: q.text, options: q.options.slice(), state: q.options.map((o, i) => (i === q.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('pn-fix' + id, F[0][1]);
      Dock.list('pn-work' + id, C.lines.map((l, i) => ({ badge: String(i + 1), color: !R.legit && i >= R.bad ? NB.PAL.redpen : NB.PAL.ink,
        lines: !R.legit && i >= R.bad ? [`Jay: ${l.text}`, `red pen: ${C.honest[i].text}${i === R.bad ? ` (${C.honest[i].property})` : ''}`] : [l.text, propOf(C, i).tag] })));
      Dock.text('pn-rule' + id, F.filter(([k]) => k === 'rule' || (R.legit && k === 'work')).map(([, t]) => t), { cls: 'note' });
      Dock.text('pn-stars' + id, F.filter(([k]) => k === 'stars').map(([, t]) => t), { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next card ▶' : 'See my stars ▶', label: 'next', aria: 'next card', go: true, onClick: () => this.next() });
    }
    /* portrait: a ? per line (free: it only shows that line's property) */
    dockChecks(id, n) {
      Dock.text('pn-chkq' + id, ["See a line's property (free): tap its ?"], { cls: 'small' });
      Dock.buttons('pn-chkb' + id, Array.from({ length: n }, (v, i) => ({ label: '? ' + (i + 1), onClick: () => this.check(i) })));
      if (this.lastCheck >= 0) Dock.text('pn-chkt' + id + '_' + this.lastCheck, [this.property(this.lastCheck).text], { cls: 'note', reveal: true });
    }

    drawIntro(docked) {
      const t0 = this.enteredAt, n = this.n, pass = this.passStars, of = this.of;
      const lines = ["Jay did his homework. Ms. Rivera has a red pen.", `${n} cards. All but one have a wrong line.`,
        'Tap the FIRST wrong line, then pick what it should say.', `${pass} of ${of} stars for the stamp.`];
      // a sample: the video's sock boxes (2x + 6 = 20, Jay divided only part), corrected by the red pen; all from the model
      if (!this._demo) {
        const V = Scale.VIDEO.s1, C = Scale.card({ id: 'demo', title: 'Sock boxes', cheat: V.cheat, method: V.method, eq: V.eq }, {});
        this._demo = { C, legit: false, bad: C.bad, n: C.lines.length, title: C.title };
      }
      const R = this._demo;
      const st = { docked, spot: false, live: false, marks: {}, checked: {}, found: R.bad, foundAt: t0 + 1.0, fix: true, fixAt: t0 + 1.4, bi: -1, show: -1, chips: false };
      if (docked) {
        this.drawCard(R, cardLay(R, 'd'), st);
        Dock.heading('pn-intro', 'Red Pen', { cls: 'big' });
        Dock.text('pn-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: "Start checking Jay's homework", go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      const L = cardLay(R, 'm');
      g.save(); g.translate(20, 0); g.translate(180, 200); g.scale(0.82, 0.82); g.translate(-180, -200); this.drawCard(R, L, st); g.restore();
      NB.title('Red Pen', 1420, 250, { size: 110, t0: t0 + 0.2, hi: 'Pen' });
      const fs = UI.text(36);
      let y = 400;
      lines.forEach((l, i) => UI._wrap(l, 900, uiF(fs, i === 3 ? 700 : 400)).forEach(tx => { NB.write(tx, 1420, y, { font: 'ui', size: fs, weight: i === 3 ? 700 : 400, align: 'center', t0: t0 + 0.5 + i * 0.3, dur: 0.45 }); y += fs * 1.45; }));
      if (this.t > 0.8 && UI.button('pn-start', 'Start', 1420, Math.min(960, y + 90), { w: 420, h: 110, primary: true })) this.begin();
      if (this.t > 1.2) NB.write(UI.hint('(or press Enter)', ''), 1420, Math.min(960, y + 90) + 80, { font: 'ui', size: UI.text(26), color: PENCIL(), align: 'center' });
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.instances.map((inst, i) => { const r = this.results[i] || { stars: 0 }, it = srcOf(inst); return { r, text: `${i + 1}. ${it.title}: ${SHORT[it.cheat] || it.cheat}` }; });
      const word = this.passed ? 'CHECKED' : 'TRY AGAIN';
      if (docked) {
        NB.title(`${s} / ${of}`, 960, 380, { size: 200, t0: this.endAt + 0.1, hi: String(s) });
        UI.stamp(word, 960, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6 });
        Dock.heading('pn-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('pn-end', [this.passed ? 'Checked! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New cards?`]);
        Dock.list('pn-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? NB.PAL.ink : NB.PAL.redpen, lines: [x.text.replace(/^\d+\. /, ''), `${x.r.stars} of 2 stars`] })));
        Dock.text('pn-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('pn-end', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      NB.title(`${s} / ${of}`, 640, 250, { size: 140, t0: this.endAt + 0.1, hi: String(s) });
      UI.stamp(word, 1330, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.1 });
      const fs = UI.text(34), rh = Math.round(Math.max(66, fs * 1.4)), bh = UI.tap(100), ch = rows.length * rh + 50;
      const cardTop = Math.round(Math.min(350, 1080 - 40 - bh - 70 - ch - 20)), top = cardTop + 25 + rh / 2;
      NB.indexCard(260, cardTop, 1400, ch, { header: 25, pitch: rh });
      rows.forEach((x, i) => {
        const y = top + i * rh;
        NB.write(x.text, 320, y, { font: 'ui', size: fs, t0: this.endAt + 0.3 + i * 0.1, dur: 0.3 });
        NB.write(`${x.r.stars} of 2`, 1580, y, { size: fs * 1.15, weight: 700, color: x.r.stars === 2 ? INK() : RED(), align: 'right', t0: this.endAt + 0.4 + i * 0.1 });
        if (x.r.stars === 2) NB.tick(1620, y, { s: 0.9, t0: this.endAt + 0.5 + i * 0.1 });
      });
      const py = cardTop + ch + 40, by = Math.min(1080 - 40 - bh / 2, py + 40 + bh / 2);
      NB.write(this.passed ? 'Checked! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp.`, 960, py, { font: 'ui', size: UI.text(34), weight: 700, align: 'center', t0: this.endAt + 0.8, dur: 0.4 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('pn-end' + i, b.label, 640 + i * 520, by, { w: 480, h: 100, primary: i === 0, secondary: i > 0 })) b.go(); });
      NB.write(`quiz code ${this.seed}`, 1860, by + bh / 2 + 24, { font: 'mono', size: UI.text(22), color: PENCIL(), align: 'right', t0: this.endAt + 0.6 });
    }
  }
  Pen.STEP = STEP; Pen.pool = pool; Pen.round = round; Pen.cardLay = cardLay; Pen.propOf = propOf; Pen.missText = missText;
  /* the live scene's solution (tests) */
  Object.defineProperty(Pen, 'solution', { get() { const s = Game.current(); return s instanceof Pen ? s.solution : null; } });
  /* the tap rects of the live scene's lines on the canvas (tests tap them): {i, x, y, w, h} centred */
  Pen.lines = () => {
    const s = Game.current();
    if (!(s instanceof Pen) || !s.R) return [];
    return s.lay(UI.docked()).rows.map(r => ({ i: r.i, x: r.cx, y: r.top + r.h / 2, w: r.w, h: r.h }));
  };
  Levels.pen = Pen;
})();
